// src/App.js
import React, { useState, useEffect, createContext } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import Home from './pages/Home';
import DrugSearch from './pages/DrugSearch';
import DrugPrices from './pages/DrugPrices';
import DigitalCard from './pages/DigitalCard';
import TopPrescriptions from './pages/TopPrescriptions';
import Pharmacies from './pages/Pharmacies';
import AdminPanel from './pages/AdminPanel';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import DisclaimerPage from './pages/DisclaimerPage';
import FAQPage from './pages/FAQPage';
import HowItWorksPage from './pages/HowItWorksPage';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';

// Utilities
import { initSourceTracking, trackVisitor, storeLandingPage } from './utils/sourceTracker';

// Styles
import './styles/App.css';

// Create context for member data
export const MemberContext = createContext();

// ScrollToTop component - scrolls to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  const [member, setMember] = useState(null);
  const [rxgoMemberId, setRxgoMemberId] = useState('');
  const [config, setConfig] = useState({
    affiliateCode: '1222',
    bin: '610378',
    pcn: 'SC1',
    group: 'SRXG1222',
    supportPhone: '(833) 600-2136',
    pharmacyPhone: '(800) 974-3135'
  });

  // Load member from localStorage on mount
  useEffect(() => {
    const savedMember = localStorage.getItem('rxgo_member');
    const savedRxgoMemberId = localStorage.getItem('rxgo_member_id');
    
    if (savedMember) {
      try {
        setMember(JSON.parse(savedMember));
      } catch (e) {
        console.error('Error parsing saved member:', e);
      }
    }
    
    if (savedRxgoMemberId) {
      setRxgoMemberId(savedRxgoMemberId);
    }

    // Fetch config from API
    fetchConfig();

    // Initialize visitor source tracking
    storeLandingPage();
    const sourceData = initSourceTracking();
    trackVisitor(sourceData);
  }, []);

  // Save member to localStorage when it changes
  useEffect(() => {
    if (member) {
      localStorage.setItem('rxgo_member', JSON.stringify(member));
    }
  }, [member]);

  // Save RxGo member ID
  useEffect(() => {
    if (rxgoMemberId) {
      localStorage.setItem('rxgo_member_id', rxgoMemberId);
    }
  }, [rxgoMemberId]);

  const fetchConfig = async () => {
    try {
      const response = await fetch('/api/config');
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
    }
  };

  const contextValue = {
    member,
    setMember,
    rxgoMemberId,
    setRxgoMemberId,
    config,
    clearMember: () => {
      setMember(null);
      localStorage.removeItem('rxgo_member');
    }
  };

  return (
    <MemberContext.Provider value={contextValue}>
      <div className="app">
        <ScrollToTop />
        <Header />
        <main className="main-content">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<DrugSearch />} />
            <Route path="/drug/:drugName" element={<DrugPrices />} />
            <Route path="/card" element={<DigitalCard />} />
            <Route path="/top-prescriptions" element={<TopPrescriptions />} />
            <Route path="/pharmacies" element={<Pharmacies />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/disclaimer" element={<DisclaimerPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
          </Routes>
        </main>
        <Footer />
        <CookieConsent />
        <ToastContainer
          position="bottom-right"
          autoClose={4000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
    </MemberContext.Provider>
  );
}

export default App;